import { useState, useEffect } from "react";
import { useTheme, products, catMeta } from "../data/data";
import { useInView, Fade, PageHeader, SmartImage } from "../utils/ui";

/* ============================================================
   LIGHTBOX MODAL — Full-screen product view
   ============================================================ */
function Lightbox({ product, onClose }) {
  const { theme } = useTheme();

  // Close on Escape key
  useEffect(() => {
    const handler = e => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handler);
      document.body.style.overflow = "";
    };
  }, [onClose]);

  if (!product) return null;
  const meta = catMeta[product.cat];

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed", inset: 0, zIndex: 9999,
        background: "rgba(5,10,20,0.92)",
        backdropFilter: "blur(8px)",
        display: "flex", alignItems: "center", justifyContent: "center",
        padding: "2rem",
        animation: "lightboxFadeIn 0.25s ease-out",
      }}
    >
      {/* Close button */}
      <button
        onClick={onClose}
        aria-label="Close"
        style={{
          position: "fixed", top: 20, right: 20, zIndex: 10,
          width: 48, height: 48, borderRadius: "50%",
          background: "rgba(255,255,255,0.15)",
          border: "1.5px solid rgba(255,255,255,0.3)",
          color: "#fff", fontSize: "1.5rem", cursor: "pointer",
          display: "flex", alignItems: "center", justifyContent: "center",
          transition: "all 0.22s",
        }}
        onMouseEnter={e => { e.currentTarget.style.background = "rgba(255,255,255,0.25)"; e.currentTarget.style.transform = "rotate(90deg)"; }}
        onMouseLeave={e => { e.currentTarget.style.background = "rgba(255,255,255,0.15)"; e.currentTarget.style.transform = "rotate(0)"; }}
      >✕</button>

      {/* Content card */}
      <div
        onClick={e => e.stopPropagation()}
        className="lightbox-card"
        style={{
          background: theme.cardBg,
          borderRadius: 16,
          maxWidth: 1100, width: "100%", maxHeight: "92vh",
          overflow: "hidden",
          boxShadow: "0 25px 80px rgba(0,0,0,0.5)",
          display: "grid",
          gridTemplateColumns: "minmax(0, 1.35fr) minmax(280px, 1fr)",
          animation: "lightboxSlideIn 0.3s ease-out",
        }}
      >
        {/* LEFT — Large image */}
        <div style={{
          position: "relative",
          minHeight: 420,
          background: product.fallbackBg || `${meta.color}20`,
          overflow: "hidden",
        }}>
          <SmartImage
            src={product.image}
            fallbackSeed={product.fallbackSeed}
            fallbackBg={product.fallbackBg}
            alt={product.name}
          />
          <span style={{
            position: "absolute", top: 20, left: 20,
            fontSize: "0.7rem", letterSpacing: "0.14em",
            textTransform: "uppercase",
            background: "rgba(255,255,255,0.96)",
            color: meta.color,
            padding: "0.45rem 1rem", borderRadius: 20,
            fontWeight: 700,
            boxShadow: "0 4px 14px rgba(0,0,0,0.18)",
          }}>{product.tag}</span>
        </div>

        {/* RIGHT — Details */}
        <div style={{
          padding: "2.5rem 2.2rem",
          display: "flex", flexDirection: "column",
          overflowY: "auto",
        }}>
          <div style={{
            fontSize: "0.65rem", letterSpacing: "0.18em", textTransform: "uppercase",
            color: meta.color, marginBottom: 10, fontWeight: 700,
          }}>{meta.label}</div>

          <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 16 }}>
            <div style={{
              width: 52, height: 52, borderRadius: 12,
              background: `${meta.color}15`,
              display: "flex", alignItems: "center", justifyContent: "center",
              fontSize: "1.7rem", flexShrink: 0,
            }}>{product.icon}</div>
            <h2 style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: "1.75rem", fontWeight: 700,
              color: theme.textPrimary, lineHeight: 1.2, margin: 0,
            }}>{product.name}</h2>
          </div>

          <div style={{ fontSize: "0.72rem", color: theme.textMuted, letterSpacing: "0.1em", marginBottom: 18 }}>
            PRODUCT #{product.num}
          </div>

          <p style={{
            fontSize: "0.95rem", color: theme.textSecondary,
            lineHeight: 1.85, marginBottom: "1.6rem",
          }}>{product.desc}</p>

          <div style={{
            background: theme.sectionAlt,
            borderRadius: 10, padding: "1rem 1.2rem",
            marginBottom: "1.8rem",
          }}>
            <div style={{ display: "grid", gap: 8, fontSize: "0.85rem" }}>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: theme.textMuted }}>Availability:</span>
                <strong style={{ color: theme.textPrimary }}>Bulk Orders</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: theme.textMuted }}>Export Ready:</span>
                <strong style={{ color: "#059669" }}>✓ Yes</strong>
              </div>
              <div style={{ display: "flex", justifyContent: "space-between" }}>
                <span style={{ color: theme.textMuted }}>Origin:</span>
                <strong style={{ color: theme.textPrimary }}>India</strong>
              </div>
            </div>
          </div>

          <a
            href={`/contact?product=${encodeURIComponent(product.name)}`}
            style={{
              display: "block",
              background: theme.accent, color: theme.textOnAccent,
              textAlign: "center", textDecoration: "none",
              padding: "0.95rem 1.5rem", borderRadius: 9,
              fontSize: "0.9rem", fontWeight: 700,
              boxShadow: `0 4px 18px ${theme.accent}45`,
              transition: "all 0.25s",
              marginTop: "auto",
            }}
            onMouseEnter={e => { e.currentTarget.style.background = theme.accentHover; e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = theme.accent; e.currentTarget.style.transform = "translateY(0)"; }}
          >
            Request Quote →
          </a>
        </div>
      </div>

      <style>{`
        @keyframes lightboxFadeIn { from {opacity:0} to {opacity:1} }
        @keyframes lightboxSlideIn { from {opacity:0; transform: scale(0.96)} to {opacity:1; transform: scale(1)} }
        @media (max-width: 768px) {
          .lightbox-card { grid-template-columns: 1fr !important; max-height: 95vh !important; }
          .lightbox-card > div:first-child { min-height: 280px !important; }
        }
      `}</style>
    </div>
  );
}

/* ============================================================
   PRODUCT CARD
   ============================================================ */
function ProductCard({ p, index, onView }) {
  const { theme } = useTheme();
  const [hov, setHov] = useState(false);
  const [ref, inView] = useInView();
  const meta = catMeta[p.cat];

  return (
    <div
      ref={ref}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
      style={{
        background: theme.cardBg,
        borderRadius: 14,
        overflow: "hidden",
        border: `1px solid ${hov ? meta.color + "60" : theme.border}`,
        boxShadow: hov ? theme.shadowHover : theme.shadowCard,
        opacity: inView ? 1 : 0,
        transform: inView
          ? (hov ? "translateY(-6px)" : "translateY(0)")
          : "translateY(22px)",
        transition: `opacity 0.6s ease ${Math.min(index * 0.06, 0.5)}s,
                     transform 0.4s ease,
                     border-color 0.28s,
                     box-shadow 0.28s,
                     background 0.35s`,
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* IMAGE — clickable, opens lightbox */}
      <div
        onClick={() => onView(p)}
        style={{
          position: "relative",
          width: "100%",
          height: 200,
          overflow: "hidden",
          background: p.fallbackBg || `${meta.color}20`,
          cursor: "zoom-in",
        }}
      >
        <div style={{
          width: "100%", height: "100%",
          transform: hov ? "scale(1.12)" : "scale(1.02)",
          transition: "transform 0.6s cubic-bezier(0.22,1,0.36,1)",
        }}>
          <SmartImage
            src={p.image}
            fallbackSeed={p.fallbackSeed}
            fallbackBg={p.fallbackBg}
            alt={p.name}
          />
        </div>

        <div style={{
          position: "absolute", inset: 0,
          background: "linear-gradient(180deg, rgba(0,0,0,0) 40%, rgba(0,0,0,0.6) 100%)",
          opacity: hov ? 1 : 0.55,
          transition: "opacity 0.4s ease",
        }} />

        {/* Zoom hint on hover */}
        <div style={{
          position: "absolute", top: "50%", left: "50%",
          transform: `translate(-50%, -50%) scale(${hov ? 1 : 0.8})`,
          opacity: hov ? 1 : 0,
          transition: "all 0.3s ease",
          background: "rgba(255,255,255,0.95)",
          color: meta.color,
          padding: "0.55rem 1.2rem",
          borderRadius: 999,
          fontSize: "0.78rem",
          fontWeight: 700,
          letterSpacing: "0.06em",
          boxShadow: "0 6px 20px rgba(0,0,0,0.25)",
          pointerEvents: "none",
          whiteSpace: "nowrap",
        }}>
          🔍 Click to view
        </div>

        <div style={{
          position: "absolute", top: 0, left: 0, right: 0,
          height: 4, background: meta.color,
          transform: hov ? "scaleX(1)" : "scaleX(0.25)",
          transformOrigin: "left",
          transition: "transform 0.32s ease",
        }} />

        <span style={{
          position: "absolute", bottom: 12, right: 12,
          fontSize: "0.62rem", letterSpacing: "0.12em",
          textTransform: "uppercase",
          background: "rgba(255,255,255,0.95)",
          color: meta.color,
          padding: "0.32rem 0.8rem", borderRadius: 20,
          fontWeight: 700,
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
        }}>{p.tag}</span>

        <div style={{
          position: "absolute", bottom: 12, left: 12,
          width: 44, height: 44, borderRadius: 12,
          background: "rgba(255,255,255,0.95)",
          display: "flex", alignItems: "center", justifyContent: "center",
          fontSize: "1.45rem",
          boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
          transform: hov ? "scale(1.08) rotate(-4deg)" : "scale(1)",
          transition: "transform 0.3s ease",
        }}>{p.icon}</div>
      </div>

      {/* TEXT BODY */}
      <div style={{ padding: "1.5rem 1.6rem 1.6rem", flex: 1, display: "flex", flexDirection: "column" }}>
        <div style={{
          fontSize: "0.63rem", letterSpacing: "0.16em", textTransform: "uppercase",
          color: meta.color, marginBottom: 7, fontWeight: 700,
        }}>{meta.label}</div>

        <h3 style={{
          fontFamily: "'Playfair Display',serif",
          fontSize: "1.18rem", fontWeight: 700,
          color: theme.textPrimary, marginBottom: "0.7rem", lineHeight: 1.3,
        }}>{p.name}</h3>

        <p style={{
          fontSize: "0.84rem", color: theme.textSecondary,
          lineHeight: 1.8, marginBottom: "1.2rem", flex: 1,
        }}>{p.desc}</p>

        {/* View Photo button */}
        <button
          onClick={() => onView(p)}
          style={{
            width: "100%",
            background: "transparent",
            color: meta.color,
            border: `1.5px solid ${meta.color}`,
            padding: "0.65rem 1rem",
            borderRadius: 8,
            fontSize: "0.8rem",
            fontWeight: 700,
            letterSpacing: "0.04em",
            cursor: "pointer",
            marginBottom: "0.9rem",
            transition: "all 0.22s",
            fontFamily: "'DM Sans',sans-serif",
          }}
          onMouseEnter={e => { e.currentTarget.style.background = meta.color; e.currentTarget.style.color = "#fff"; }}
          onMouseLeave={e => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = meta.color; }}
        >
          👁️ View Full Photo &amp; Details
        </button>

        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          paddingTop: "0.9rem", borderTop: `1px solid ${theme.border}`,
        }}>
          <span style={{ fontSize: "0.72rem", color: theme.textMuted }}>
            Product #{p.num}
          </span>
          <span style={{
            fontSize: "0.72rem",
            background: theme.badgeBg, color: theme.badgeText,
            padding: "0.2rem 0.6rem", borderRadius: 10, fontWeight: 600,
          }}>Bulk Available</span>
        </div>
      </div>
    </div>
  );
}

/* ============================================================
   PRODUCTS PAGE
   ============================================================ */
export default function Products() {
  const { theme } = useTheme();
  const [filter, setFilter] = useState("all");
  const [activeProduct, setActiveProduct] = useState(null);

  const tabs = [
    { key: "all", label: "All Products", emoji: "📦", count: products.length },
    { key: "industrial", label: "Industrial", emoji: "⚙️", count: products.filter(p => p.cat === "industrial").length },
    { key: "agro", label: "Agro", emoji: "🌾", count: products.filter(p => p.cat === "agro").length },
    { key: "spices", label: "Spices", emoji: "🌶️", count: products.filter(p => p.cat === "spices").length },
  ];

  const filtered = filter === "all" ? products : products.filter(p => p.cat === filter);

  return (
    <div style={{ paddingTop: 70, minHeight: "100vh" }}>
      <PageHeader
        theme={theme}
        tag="Our Portfolio"
        title="What We Supply"
        subtitle="Premium industrial raw materials and agro commodities — all export-ready with standardized international quality. Click any product to see the full photo."
        image="https://images.unsplash.com/photo-1494412519320-aa613dfb7738?auto=format&fit=crop&w=1600&q=80"
      />

      <div style={{ background: theme.cardBg, borderBottom: `1px solid ${theme.border}`, position: "sticky", top: 70, zIndex: 50, transition: "background 0.35s" }}>
        <div style={{ maxWidth: 1280, margin: "0 auto", padding: "0 5vw", display: "flex", overflowX: "auto", gap: 0 }}>
          {tabs.map(tab => (
            <button key={tab.key} onClick={() => setFilter(tab.key)} style={{
              display: "flex", alignItems: "center", gap: 7,
              background: "none", border: "none", cursor: "pointer",
              padding: "1rem 1.4rem", fontSize: "0.82rem", fontWeight: filter === tab.key ? 700 : 500,
              color: filter === tab.key ? theme.primary : theme.textMuted,
              borderBottom: `3px solid ${filter === tab.key ? theme.accent : "transparent"}`,
              fontFamily: "'DM Sans',sans-serif", whiteSpace: "nowrap", transition: "all 0.22s",
            }}
              onMouseEnter={e => { if (filter !== tab.key) e.currentTarget.style.color = theme.textSecondary; }}
              onMouseLeave={e => { if (filter !== tab.key) e.currentTarget.style.color = theme.textMuted; }}>
              <span>{tab.emoji}</span>
              <span>{tab.label}</span>
              <span style={{ background: filter === tab.key ? `${theme.accent}20` : theme.sectionAlt, color: filter === tab.key ? theme.accent : theme.textMuted, fontSize: "0.68rem", padding: "0.1rem 0.55rem", borderRadius: 10, fontWeight: 700, transition: "all 0.22s" }}>{tab.count}</span>
            </button>
          ))}
        </div>
      </div>

      <div style={{ background: theme.sectionAlt, padding: "0.9rem 5vw", textAlign: "center", borderBottom: `1px solid ${theme.border}` }}>
        <span style={{ fontSize: "0.78rem", color: theme.textSecondary, letterSpacing: "0.04em" }}>
          🔍 <strong style={{ color: theme.primary }}>Click any product</strong> to see the full-size photo and details
        </span>
      </div>

      <div style={{ maxWidth: 1280, margin: "0 auto", padding: "3rem 5vw" }}>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(290px, 1fr))", gap: "1.7rem" }}>
          {filtered.map((p, i) => (
            <ProductCard
              key={p.id}
              p={p}
              index={i}
              onView={setActiveProduct}
            />
          ))}
        </div>
      </div>

      <div style={{ background: theme.sectionAlt, borderTop: `1px solid ${theme.border}`, padding: "4rem 5vw", textAlign: "center", transition: "background 0.35s" }}>
        <Fade>
          <h3 style={{ fontFamily: "'Playfair Display',serif", fontSize: "1.9rem", fontWeight: 700, color: theme.textPrimary, marginBottom: "0.75rem" }}>Interested in Bulk Orders?</h3>
          <p style={{ fontSize: "0.95rem", color: theme.textSecondary, marginBottom: "2rem", maxWidth: 440, margin: "0 auto 2rem", lineHeight: 1.8 }}>Contact us for pricing, availability, and international shipping details.</p>
          <a href="/contact" style={{ background: theme.accent, color: theme.textOnAccent, textDecoration: "none", padding: "0.9rem 2.2rem", borderRadius: 8, fontSize: "0.9rem", fontWeight: 700, boxShadow: `0 4px 18px ${theme.accent}40`, display: "inline-block", transition: "all 0.25s" }}
            onMouseEnter={e => { e.currentTarget.style.background = theme.accentHover; e.currentTarget.style.transform = "translateY(-3px)"; }}
            onMouseLeave={e => { e.currentTarget.style.background = theme.accent; e.currentTarget.style.transform = "translateY(0)"; }}>
            Request a Quote →
          </a>
        </Fade>
      </div>

      {/* LIGHTBOX — renders when a product is active */}
      {activeProduct && (
        <Lightbox product={activeProduct} onClose={() => setActiveProduct(null)} />
      )}
    </div>
  );
}
